import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import type { UnreconciledOfflineItem } from '@/types'

/**
 * The manager worklist for food that left the kitchen unbilled (F-CTRL-N35).
 *
 * This screen is load-bearing, not informational: an open item HARD-BLOCKS Day
 * Close, and this is the only way to clear one. Without it a single failed
 * offline order would leave a restaurant unable to close its day at all.
 */

/** Format a tenant-currency figure for a manager reading a variance. */
function money(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/**
 * Turn a stable failure code into something a manager can act on.
 *
 * The two money codes used to collapse into the default branch, which printed
 * the raw code — `#OFF-B4BB-002 could not be posted (offline_payment_failed).`
 * — at a manager being asked to decide about cash in a drawer. They are
 * different problems and they need different sentences: one payment never
 * recorded at all, one recorded at a different amount. The backend has always
 * sent both figures; nothing displayed them (F-FE-N151).
 */
function explainFailure(
  code: string,
  orderNumber: string,
  collected?: number,
  settled?: number,
): string {
  const ref = orderNumber ? `#${orderNumber}` : 'The order'
  switch (code) {
    case 'offline_payment_failed':
      return collected != null
        ? `${money(collected)} was collected for ${ref.toLowerCase()} during the outage, but the payment could not be recorded. The money is in the till and the order is still unpaid.`
        : `A payment taken for ${ref.toLowerCase()} during the outage could not be recorded.`
    case 'offline_payment_amount_mismatch': {
      if (collected == null || settled == null) {
        return `${ref} settled at a different amount from what was collected offline.`
      }
      const diff = settled - collected
      const direction = diff > 0 ? 'short' : 'over'
      return `${ref} billed ${money(settled)} but ${money(collected)} was collected offline — the till is ${money(Math.abs(diff))} ${direction}. A price or tax rate moved during the outage.`
    }
    case 'order_not_editable':
      return `${ref} was already paid or voided on another terminal while this one was offline.`
    case 'order_not_found':
      return `${ref} no longer exists — it was removed while this terminal was offline.`
    case 'table_has_active_order':
      return `The table was seated by another terminal while this one was offline, so ${ref.toLowerCase()} could not be opened.`
    case 'offline_order_duplicate':
      return `${ref} was already synced by another attempt.`
    default:
      return `${ref} could not be posted (${code}).`
  }
}

function ItemRow({ item, onResolved }: { item: UnreconciledOfflineItem; onResolved: () => void }) {
  const { toast } = useToast()
  const [note, setNote] = useState('')

  const resolve = useMutation({
    mutationFn: (action: 'billed' | 'written_off') =>
      apiClient.resolveUnreconciledOffline(item.id, action, note.trim()),
    onSuccess: (res) => {
      if (!res.success) {
        toast({ title: 'Could not resolve', description: res.message, variant: 'destructive' })
        return
      }
      toast({ title: 'Resolved', description: 'The item no longer blocks Day Close.' })
      onResolved()
    },
    onError: (e: unknown) => {
      toast({
        title: 'Could not resolve',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      })
    },
  })

  const lineCount = Array.isArray(item.lines) ? item.lines.length : 0
  const noteMissing = note.trim() === ''

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-4 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <div className="font-medium">
              {item.order_number ? `Order #${item.order_number}` : 'Offline order'} —{' '}
              {lineCount} item{lineCount === 1 ? '' : 's'} sent to the kitchen
            </div>
            <div className="text-sm text-muted-foreground">
              {explainFailure(
                item.failure_code,
                item.order_number,
                item.collected_amount,
                item.settled_amount,
              )}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Taken {new Date(item.captured_at).toLocaleString()}
            </div>
          </div>

          {lineCount > 0 && (
            <ul className="rounded border bg-background/60 p-2 text-sm">
              {item.lines.map((l, i) => (
                <li key={i} className="tabular-nums">
                  {/*
                    F-FE-N151: the name, never the raw id. This list is the only
                    record of what the food WAS, shown while asking a manager to
                    write it off — and it printed
                    `1 x 806761da-7bfd-4c7c-b755-0808b2126514`. The name is
                    snapshotted at capture and, for rows queued before that
                    shipped, resolved by the list endpoint's join. The short id
                    survives only when a deleted product leaves neither.
                  */}
                  {l.quantity} ×{' '}
                  {l.name ? (
                    <span className="font-medium">{l.name}</span>
                  ) : (
                    <span
                      className="font-mono text-xs text-muted-foreground"
                      title={l.productId}
                    >
                      item {l.productId.slice(0, 8)} (deleted from the menu)
                    </span>
                  )}
                  {l.special_instructions ? (
                    <span className="text-muted-foreground"> — {l.special_instructions}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          <Input
            placeholder="Why? e.g. billed to order #20260805-052, or comped — kitchen error"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={noteMissing || resolve.isPending}
              onClick={() => resolve.mutate('billed')}
            >
              {resolve.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Billed to another order
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={noteMissing || resolve.isPending}
              onClick={() => resolve.mutate('written_off')}
            >
              Write off
            </Button>
            {noteMissing && (
              <span className="self-center text-xs text-muted-foreground">
                A reason is required — it is the only record of what happened to this food.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function UnreconciledOfflineItems() {
  const queryClient = useQueryClient()
  const { data, isPending } = useQuery({
    queryKey: ['offline', 'unreconciled'],
    queryFn: () => apiClient.listUnreconciledOffline(),
    refetchInterval: 60_000,
  })

  const items = data?.data?.items ?? []
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['offline', 'unreconciled'] })

  // Stay out of the way when there is nothing to do — this is an exception
  // queue, not a dashboard tile.
  if (isPending || items.length === 0) return null

  return (
    <Card className="border-amber-300 dark:border-amber-800">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          Fired but unbilled ({items.length})
        </CardTitle>
        <CardDescription>
          These items were sent to the kitchen from a terminal that was offline, and
          could not be billed when it reconnected. The food was made. Decide what
          happened to it — <strong>Day Close is blocked until you do</strong>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map((it) => (
          <ItemRow key={it.id} item={it} onResolved={invalidate} />
        ))}
      </CardContent>
    </Card>
  )
}
